import {
    PortfolioCentralRateGroupUpdateParameters,
    SettlementExtent,
    useRepositoryMutation,
} from '@mews-commander/data-access';
import { CommanderFormProps, EnumSelectField, useHandledDialogFormControls } from '@mews-commander/form';
import { createExperimentalZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import { Stack } from '@optimus-web/core';
import { ConfigurationFieldName, SelectedRateGroup } from '../../../../types';
import { useSelectedRateGroupContext } from '../../../../contexts/CurrentRateGroupContext';
import { MaximumTimeForPaymentField } from '../../../../../rateGroups/components/forms/fields/MaximumTimeForPaymentField';
import { PaymentConfigurationOptionsField } from '../../../../../rateGroups';
import { ConfigurationFormValues, configurationSchema } from './schemas/configurationFormSchema';

type Params = Pick<SelectedRateGroup, 'options' | 'settlementExtent' | 'settlementMaximumTimeUnits'>;

export const EditConfigurationDialogForm = ({
    options,
    settlementExtent,
    settlementMaximumTimeUnits,
    ...other
}: CommanderFormProps & Params) => {
    const { mutateAsync } = useRepositoryMutation('portfolioCentralRateGroups', 'update', {
        invalidateQueries: { portfolioCentralRateGroups: ['getAll'] },
        onSuccess: () => {
            showSuccessAlert({
                title: lt('RateGroupUpdatedSuccessMessage'),
            });
        },
    });
    const { id, name, ordering, shortName, description, rateGroupKey } = useSelectedRateGroupContext();

    const { renderFormContent } = useHandledDialogFormControls<
        ConfigurationFormValues,
        PortfolioCentralRateGroupUpdateParameters
    >({
        ...other,
        values: {
            [ConfigurationFieldName.MaximumTime]: settlementMaximumTimeUnits,
            [ConfigurationFieldName.Extent]: settlementExtent,
            [ConfigurationFieldName.Options]: options.filter(
                (option) => option !== 'RequirePaymentCardDuringOnlineCheckIn'
            ),
        },
        onSubmit: async (values) =>
            mutateAsync({
                centralRateGroupId: id,
                name,
                shortName,
                description,
                ordering,
                rateGroupKey,
                settlementMaximumTimeUnits: values.settlementMaximumTimeUnits,
                settlementExtent: values.settlementExtent,
                options: values.options,
            }),
        resolver: createExperimentalZodFormResolver(configurationSchema),
    });

    return renderFormContent(
        <Stack vertical spacing="300">
            <MaximumTimeForPaymentField name={ConfigurationFieldName.MaximumTime} autoFocus />
            <EnumSelectField
                enumClass={SettlementExtent}
                enumPrefix="SettlementExtent"
                label={lt('SettlementExtent')}
                multiselect
                name={ConfigurationFieldName.Extent}
                omitHint
                required
            />
            <PaymentConfigurationOptionsField name={ConfigurationFieldName.Options} />
        </Stack>
    );
};
